/* ==========================================================================
   agv-sticky-buy.js - the sticky buy bar on phones (29 September 2026, U3).
   Markup: snippets/agv-sticky-buy.liquid. Styles: assets/agv-pdp.css.

   The bar mirrors the product page's own primary action and never decides
   anything itself:
     - mode form: its button clicks the page's add to cart button
       ([data-agv-atc]), so the existing product form submits with its own
       checks; there is no second form. Label and disabled state are copied
       from that button;
     - mode link: its link follows the page's enquiry link
       (.agv-avail__link--primary), with the address that link has at the
       moment of the tap. Label copied from it;
     - price: the text of the page's price slot, copied as it stands, with
       its concealed (Sold, Price on enquiry) and withheld (not offered)
       states. The bar never reads a price from anywhere else.
   Kept in step on agv:variant:change and on every option change.

   Shown at 749px and below once the primary action has scrolled up out of
   view (under the fixed header and the sticky breadcrumb); hidden when the
   action is visible again or the footer (#footerGroup) comes into view.
   Two IntersectionObservers, no scroll listener. While the bar can show,
   <html> carries agv-has-sbar, which pads the page bottom so the bar never
   covers the footer or a focused control.

   No IntersectionObserver, no primary action, or no JavaScript: the bar
   stays hidden and the page is exactly as before.
   ========================================================================== */
(function () {
  'use strict';

  var PHONE = '(max-width: 749px)';

  function text(el) {
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function init(bar) {
    if (!bar || bar.hasAttribute('data-agv-sbar-ready')) { return; }
    if (!('IntersectionObserver' in window) || !window.matchMedia) { return; }
    var root = bar.closest('[data-agv-pdp]');
    if (!root) { return; }

    var mode = bar.getAttribute('data-mode') === 'link' ? 'link' : 'form';
    var primary = mode === 'link'
      ? root.querySelector('.agv-buy .agv-avail__link--primary')
      : root.querySelector('.agv-buy [data-agv-atc]');
    var btn = bar.querySelector('[data-agv-sbar-btn]');
    if (!primary || !btn) { return; }
    bar.setAttribute('data-agv-sbar-ready', '');

    var priceIn = root.querySelector('.agv-buy .pr');
    var priceOut = bar.querySelector('[data-agv-sbar-price]');
    var mq = window.matchMedia(PHONE);
    var past = false;
    var footerIn = false;

    // Copy the page's current state into the bar. Idempotent.
    var sync = function () {
      if (priceIn && priceOut) {
        var p = text(priceIn);
        if (priceOut.textContent !== p) { priceOut.textContent = p; }
        priceOut.classList.toggle('is-concealed', priceIn.hasAttribute('data-agv-price-concealed'));
        priceOut.classList.toggle('is-void', priceIn.classList.contains('is-void'));
      }
      var label = text(primary);
      if (btn.textContent !== label) { btn.textContent = label; }
      if (mode === 'link') {
        var href = primary.getAttribute('href');
        if (href && btn.getAttribute('href') !== href) { btn.setAttribute('href', href); }
      } else {
        btn.disabled = primary.disabled;
      }
    };

    var update = function () {
      var on = mq.matches && past && !footerIn;
      if (on !== bar.classList.contains('is-on')) {
        if (on) { sync(); }
        bar.classList.toggle('is-on', on);
      }
    };

    // Where the visible page starts: under the fixed header and the sticky
    // breadcrumb, whose own computed top is the header's height.
    var topOffset = function () {
      var sub = root.querySelector('.agv-sub');
      if (!sub) { return 0; }
      var top = parseFloat(window.getComputedStyle(sub).top) || 0;
      return Math.max(0, Math.round(top + sub.offsetHeight));
    };

    var io = null;
    var observe = function () {
      if (io) { io.disconnect(); }
      var top = topOffset();
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          // Out of view ABOVE the visible page only: a button still below
          // the fold on first load does not bring the bar up.
          var edge = e.rootBounds ? e.rootBounds.top : top;
          past = !e.isIntersecting && e.boundingClientRect.bottom <= edge;
        });
        update();
      }, { rootMargin: '-' + top + 'px 0px 0px 0px', threshold: 0 });
      io.observe(primary);
    };

    var footer = document.getElementById('footerGroup') || document.querySelector('footer');
    if (footer) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { footerIn = e.isIntersecting; });
        update();
      }, { threshold: 0 }).observe(footer);
    }

    // The header and breadcrumb change height with the width; phone
    // toolbars resize the height constantly, so only a width change
    // re-measures.
    var lastW = window.innerWidth;
    var timer = null;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastW) { return; }
      lastW = window.innerWidth;
      clearTimeout(timer);
      timer = setTimeout(observe, 150);
    });
    if (mq.addEventListener) { mq.addEventListener('change', update); }
    else if (mq.addListener) { mq.addListener(update); }

    root.addEventListener('agv:variant:change', sync);
    // Option changes bubble here after the picker has repainted, including
    // a combination that is not offered, which fires no variant event.
    root.addEventListener('change', sync);

    btn.addEventListener('click', function (ev) {
      if (mode === 'link') {
        // Follow the enquiry link as it stands at the moment of the tap.
        var href = primary.getAttribute('href');
        if (href) { btn.setAttribute('href', href); }
        return;
      }
      ev.preventDefault();
      if (!primary.disabled) { primary.click(); }
    });

    sync();
    observe();
    bar.hidden = false;
    document.documentElement.classList.add('agv-has-sbar');
  }

  function initAll(scope) {
    var bars = (scope || document).querySelectorAll('[data-agv-sbar]');
    for (var i = 0; i < bars.length; i++) { init(bars[i]); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  // Theme editor: a re-rendered section brings a fresh bar.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
